/**
 * Persists in-progress "new prescription" state (react-hook-form fields + selected exercises).
 * Skips while editing an existing prescription (`prescriptionId` set).
 *
 * Single storage key; migrates legacy `prescription-draft-form` + `prescription-selected-cards` on read.
 */

const STORAGE_KEY = 'prescription-builder-draft';
const LEGACY_FORM_KEY = 'prescription-draft-form';
const LEGACY_CARDS_KEY = 'prescription-selected-cards';
const VERSION = 1;

function tryParseJson(raw) {
  if (raw === null || raw === undefined || raw === '') {
    return null;
  }
  try {
    const p = JSON.parse(raw);
    return p && typeof p === 'object' ? p : null;
  } catch {
    return null;
  }
}

function draftHasContent(draft) {
  if (!draft || typeof draft !== 'object') {
    return false;
  }
  const { form, selectedCard } = draft;
  if (Array.isArray(selectedCard) && selectedCard.length > 0) {
    return true;
  }
  if (form && typeof form === 'object' && Object.keys(form).length > 0) {
    return true;
  }
  return false;
}

function readLegacyMerged() {
  const legacyForm = tryParseJson(localStorage.getItem(LEGACY_FORM_KEY));
  const legacyCards = tryParseJson(localStorage.getItem(LEGACY_CARDS_KEY));
  const form =
    legacyForm && typeof legacyForm === 'object' && !Array.isArray(legacyForm)
      ? { ...legacyForm }
      : {};
  const selectedCard = Array.isArray(legacyCards) ? legacyCards : [];
  if (Object.keys(form).length === 0 && selectedCard.length === 0) {
    return null;
  }
  return { version: VERSION, form, selectedCard };
}

/**
 * @returns {{ version: number, form: Record<string, unknown>, selectedCard: unknown[] } | null}
 */
export function getPrescriptionBuilderDraft() {
  try {
    const unified = tryParseJson(localStorage.getItem(STORAGE_KEY));
    if (
      unified &&
      typeof unified.form === 'object' &&
      unified.form !== null &&
      Array.isArray(unified.selectedCard)
    ) {
      return unified;
    }
  } catch {
    // fall through to legacy
  }
  return readLegacyMerged();
}

export function savePrescriptionBuilderDraft({ form, selectedCard }, prescriptionId) {
  if (prescriptionId !== undefined && prescriptionId !== null) {
    return;
  }
  try {
    const payload = {
      version: VERSION,
      form: form && typeof form === 'object' ? form : {},
      selectedCard: Array.isArray(selectedCard) ? selectedCard : [],
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    localStorage.removeItem(LEGACY_FORM_KEY);
    localStorage.removeItem(LEGACY_CARDS_KEY);
  } catch {
    // ignore quota / private mode
  }
}

/**
 * Update selected exercises after modal save when a unified new-prescription draft exists.
 * Does not write legacy keys (avoids polluting the next new-prescription restore after edit).
 */
export function patchPrescriptionBuilderDraftSelectedCard(selectedCard) {
  try {
    const rawUnified = localStorage.getItem(STORAGE_KEY);
    const existingUnified = tryParseJson(rawUnified);
    if (
      existingUnified &&
      typeof existingUnified.form === 'object' &&
      existingUnified.form !== null &&
      Array.isArray(existingUnified.selectedCard)
    ) {
      savePrescriptionBuilderDraft({ form: existingUnified.form, selectedCard }, undefined);
    }
  } catch {
    // ignore
  }
}

export function clearPrescriptionBuilderDraft() {
  try {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(LEGACY_FORM_KEY);
    localStorage.removeItem(LEGACY_CARDS_KEY);
  } catch {
    // ignore
  }
}

const EDIT_DRAFT_KEY_PREFIX = 'prescription-edit-draft-';

export function saveEditPrescriptionDraft(prescriptionId, selectedCard) {
  if (!prescriptionId) {
    return;
  }
  try {
    const existing = tryParseJson(sessionStorage.getItem(`${EDIT_DRAFT_KEY_PREFIX}${prescriptionId}`));
    sessionStorage.setItem(
      `${EDIT_DRAFT_KEY_PREFIX}${prescriptionId}`,
      JSON.stringify({
        version: 2,
        selectedCard: Array.isArray(selectedCard) ? selectedCard : [],
        form: existing?.form ?? {},
      })
    );
  } catch {
    // ignore
  }
}

export function saveEditPrescriptionFormDraft(prescriptionId, form) {
  if (!prescriptionId) return;
  try {
    const existing = tryParseJson(sessionStorage.getItem(`${EDIT_DRAFT_KEY_PREFIX}${prescriptionId}`));
    sessionStorage.setItem(
      `${EDIT_DRAFT_KEY_PREFIX}${prescriptionId}`,
      JSON.stringify({
        version: 2,
        selectedCard: existing?.selectedCard ?? [],
        form: form && typeof form === 'object' ? form : {},
      })
    );
  } catch {
    // ignore
  }
}

export function getEditPrescriptionDraft(prescriptionId) {
  if (!prescriptionId) {
    return null;
  }
  try {
    const raw = sessionStorage.getItem(`${EDIT_DRAFT_KEY_PREFIX}${prescriptionId}`);
    const parsed = tryParseJson(raw);
    if (parsed && Array.isArray(parsed.selectedCard) && parsed.selectedCard.length > 0) {
      return parsed.selectedCard;
    }
  } catch {
    // ignore
  }
  return null;
}

export function getEditPrescriptionFormDraft(prescriptionId) {
  if (!prescriptionId) return null;
  try {
    const raw = sessionStorage.getItem(`${EDIT_DRAFT_KEY_PREFIX}${prescriptionId}`);
    const parsed = tryParseJson(raw);
    if (parsed?.form && typeof parsed.form === 'object' && Object.keys(parsed.form).length > 0) {
      return parsed.form;
    }
  } catch {
    // ignore
  }
  return null;
}

export function clearEditPrescriptionDraft(prescriptionId) {
  if (!prescriptionId) {
    return;
  }
  try {
    sessionStorage.removeItem(`${EDIT_DRAFT_KEY_PREFIX}${prescriptionId}`);
  } catch {
    // ignore
  }
}

export { draftHasContent };
